import { useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { edgeHivas, EdgeHiba } from '../lib/edge.ts';
import { SZEREP_CIMKE, type TagSzerep } from '../lib/tipusok.ts';
import { Gomb, Mezo, Valaszto } from '../ui/elemek.tsx';
import { ujMeghivoLejarata } from './meghivoAllapot.ts';
import { MEGHIVAS_ALAP_HIBA, MEGHIVAS_HIBA_SZOVEG } from './weboldalKollegak.ts';

const EREDMENY_SZOVEG: Record<string, (email: string) => string> = {
  meghivva: (email) => `Meghívót küldtünk: ${email}. ${ujMeghivoLejarata(new Date())}-ig érvényes, addig kell elfogadnia.`,
  hozzaadva: () => 'Már van fiókja, hozzáadtuk az irodához, és értesítettük.',
  ujraaktivalva: () => 'Újra aktív az irodában.',
};

export default function MeghivoUrlap({ officeId }: { officeId: string }) {
  const qc = useQueryClient();
  const [nyitva, setNyitva] = useState(false);
  const [nev, setNev] = useState('');
  const [email, setEmail] = useState('');
  const [telefon, setTelefon] = useState('');
  const [szerep, setSzerep] = useState<TagSzerep>('ertekesito');
  const [folyamatban, setFolyamatban] = useState(false);
  const [hiba, setHiba] = useState<string | null>(null);
  const [siker, setSiker] = useState<string | null>(null);

  async function kuldes(e: FormEvent) {
    e.preventDefault();
    setHiba(null);
    setSiker(null);
    setFolyamatban(true);
    try {
      const cim = email.trim();
      const valasz = await edgeHivas<{ eredmeny: string }>('admin-meghivas', {
        officeId, email: cim, nev: nev.trim(), telefon: telefon.trim() === '' ? undefined : telefon.trim(), szerep,
      });
      setSiker((EREDMENY_SZOVEG[valasz.eredmeny] ?? (() => 'Kész.'))(cim));
      setNev(''); setEmail(''); setTelefon(''); setSzerep('ertekesito');
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['kollegak', officeId] }),
        qc.invalidateQueries({ queryKey: ['kollegak-meghivok', officeId] }),
      ]);
    } catch (err) {
      setHiba(err instanceof EdgeHiba ? (MEGHIVAS_HIBA_SZOVEG[err.kod] ?? MEGHIVAS_ALAP_HIBA) : MEGHIVAS_ALAP_HIBA);
    } finally {
      setFolyamatban(false);
    }
  }

  if (!nyitva) {
    return (
      <div className="grid gap-2 sm:justify-items-end">
        <Gomb onClick={() => { setNyitva(true); setSiker(null); }} className="sm:w-fit">Kolléga meghívása</Gomb>
        {siker && <p role="status" className="rounded-xl border border-siker px-3.5 py-2.5 text-sm text-siker">{siker}</p>}
      </div>
    );
  }
  return (
    <section className="w-full rounded-[18px] border border-vonal bg-hatter p-5 sm:max-w-md">
      <form onSubmit={(e) => void kuldes(e)} className="grid gap-4">
        <h2 className="text-lg font-medium tracking-[-0.015em]">Kolléga meghívása</h2>
        <Mezo label="Név" value={nev} onChange={(e) => setNev(e.target.value)} required autoComplete="off" />
        <Mezo label="E-mail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="off"
          suly="A meghívó egy óráig érvényes. Ha a kolléga lekéste, a nevénél újraküldheted." />
        <Mezo label="Telefon (nem kötelező)" type="tel" value={telefon} onChange={(e) => setTelefon(e.target.value)}
          suly="Belső elérhetőség az irodának; a nyilvános oldalon nem jelenik meg." autoComplete="off" />
        <Valaszto label="Szerep" value={szerep} onChange={(e) => setSzerep(e.target.value as TagSzerep)}>
          <option value="ertekesito">{SZEREP_CIMKE.ertekesito}</option>
          <option value="vezeto">{SZEREP_CIMKE.vezeto}</option>
        </Valaszto>
        {hiba && <p role="alert" className="rounded-xl border border-hiba bg-hiba-hatter px-3.5 py-2.5 text-sm text-hiba">{hiba}</p>}
        <div className="flex flex-wrap gap-2">
          <Gomb type="submit" toltes={folyamatban}>Meghívó küldése</Gomb>
          <Gomb valtozat="szoveg" onClick={() => setNyitva(false)} disabled={folyamatban}>Mégse</Gomb>
        </div>
      </form>
    </section>
  );
}
